import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { UserTable } from '../users/user-table.jsx';
import { UserFormModal } from '../users/user-form-modal.jsx';
import { ResetPasswordModal } from '../users/reset-password-modal.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { useToast } from '../ui/toast.jsx';
import { api, ApiError } from '../../api/index.js';

const ALL = 'all';

export function UsersPage({ currentUser }) {
  const toast = useToast();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(ALL);

  const [formState, setFormState] = useState({ open: false, mode: 'create', initial: null });
  const [resetState, setResetState] = useState({ open: false, user: null });
  const [deleteState, setDeleteState] = useState({ open: false, user: null });

  const refresh = useCallback(async () => {
    try {
      const { users: list } = await api.users.list();
      setUsers(list);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== ALL && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        u.login.toLowerCase().includes(q) ||
        (u.displayName || '').toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter]);

  const counts = useMemo(() => {
    if (!users) return { total: 0, admins: 0, active: 0 };
    return {
      total: users.length,
      admins: users.filter((u) => u.role === 'admin' && u.isActive).length,
      active: users.filter((u) => u.isActive).length,
    };
  }, [users]);

  function openCreate() {
    setFormState({ open: true, mode: 'create', initial: null });
  }

  function openEdit(user) {
    setFormState({ open: true, mode: 'edit', initial: user });
  }

  async function handleSave(payload) {
    if (formState.mode === 'create') {
      await api.users.create(payload);
      toast.success(`User "${payload.login}" created.`);
    } else {
      await api.users.update(formState.initial.id, payload);
      toast.success('User updated.');
    }
    setFormState({ open: false, mode: 'create', initial: null });
    await refresh();
  }

  async function handleResetPassword(password) {
    await api.users.setPassword(resetState.user.id, password);
    toast.success(`Password reset for "${resetState.user.login}". Active sessions invalidated.`);
    setResetState({ open: false, user: null });
  }

  async function handleDelete() {
    try {
      await api.users.remove(deleteState.user.id);
      toast.success(`User "${deleteState.user.login}" deleted.`);
      setDeleteState({ open: false, user: null });
      await refresh();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.',
      );
    }
  }

  return (
    <PageShell
      icon="fa-users"
      title="Users"
      subtitle="Operator accounts and access control"
      actions={
        currentUser?.role === 'admin' && (
          <button type="button" onClick={openCreate} className="btn-primary">
            <i className="fa-solid fa-user-plus" aria-hidden="true" />
            Create user
          </button>
        )
      }
    >
      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Total" value={counts.total} icon="fa-users" />
        <StatCard label="Active" value={counts.active} icon="fa-circle-check" tone="success" />
        <StatCard label="Active admins" value={counts.admins} icon="fa-user-shield" tone="accent" />
      </div>

      {/* Filters */}
      <div className="card mb-4 flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <i
            className="fa-solid fa-magnifying-glass pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mist"
            aria-hidden="true"
          />
          <input
            type="search"
            className="field pl-10"
            placeholder="Search by login or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1 rounded-2xl border border-line bg-white p-1">
          {[
            { v: ALL, label: 'All' },
            { v: 'admin', label: 'Admins' },
            { v: 'user', label: 'Users' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setRoleFilter(opt.v)}
              className={
                roleFilter === opt.v
                  ? 'rounded-xl bg-accent-soft px-3 py-1.5 text-sm font-semibold text-accent'
                  : 'rounded-xl px-3 py-1.5 text-sm font-medium text-mist hover:text-ink'
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {error && (
        <div role="alert" className="card mb-4 border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      {users === null ? (
        <div className="card px-6 py-16 text-center text-mist">
          <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
          <div className="text-sm">Loading users…</div>
        </div>
      ) : (
        <UserTable
          users={filtered}
          currentUserId={currentUser?.id}
          viewerTimezone={currentUser?.timezone || 'UTC'}
          onEdit={openEdit}
          onResetPassword={(user) => setResetState({ open: true, user })}
          onDelete={(user) => setDeleteState({ open: true, user })}
        />
      )}

      {/* Modals */}
      <UserFormModal
        open={formState.open}
        mode={formState.mode}
        initial={formState.initial}
        currentUserId={currentUser?.id}
        onClose={() => setFormState({ open: false, mode: 'create', initial: null })}
        onSave={handleSave}
      />

      <ResetPasswordModal
        open={resetState.open}
        user={resetState.user}
        onClose={() => setResetState({ open: false, user: null })}
        onConfirm={handleResetPassword}
      />

      <ConfirmDialog
        open={deleteState.open}
        title={`Delete ${deleteState.user?.login || 'user'}?`}
        message={`This permanently removes the operator account and signs out every active session. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleteState({ open: false, user: null })}
        onConfirm={handleDelete}
      />
    </PageShell>
  );
}

function StatCard({ label, value, icon, tone = 'mist' }) {
  const tones = {
    mist:    'bg-canvas text-mist',
    success: 'bg-success-soft text-success',
    accent:  'bg-accent-soft text-accent',
  };
  return (
    <div className="card flex items-center justify-between px-5 py-4">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
        <div className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">{value}</div>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone]}`}>
        <i className={`fa-solid ${icon} text-lg`} aria-hidden="true" />
      </div>
    </div>
  );
}
